import { useState, useEffect, useRef } from 'react';

// Azai Studios Nav Component - matches www.azaistudios.com exactly
export default function Nav() {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const navRef = useRef(null);
  const lastScrollY = useRef(0);

  // Close menu on escape key, click outside, and handle body scroll lock
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Hide nav on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollThreshold = 10; // Minimum scroll distance to trigger

      // Don't hide if menu is open
      if (isOpen) {
        setIsVisible(true);
        lastScrollY.current = currentScrollY;
        return;
      }

      // Always show nav at the top of the page
      if (currentScrollY < 50) {
        setIsVisible(true);
        lastScrollY.current = currentScrollY;
        return;
      }

      // Determine scroll direction
      if (currentScrollY > lastScrollY.current + scrollThreshold) {
        // Scrolling down
        setIsVisible(false);
      } else if (currentScrollY < lastScrollY.current - scrollThreshold) {
        // Scrolling up
        setIsVisible(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  const navLinks = [
    { href: 'https://www.azaistudios.com/work', label: 'Our Work' },
    { href: 'https://www.azaistudios.com/about', label: 'Our Firm' },
    { href: 'https://www.azaistudios.com/approach', label: 'Our Ways' },
    { href: 'https://www.azaistudios.com/journal', label: 'Journal' },
    { href: 'https://www.azaistudios.com/contact', label: 'Contact' },
  ];

  return (
    <section className={`section-nav ${isVisible ? '' : 'is-hidden'}`}>
      <div className="padding-global">
        <div className="container-large">
          <div className="inline-block flex">
            <nav className="nav" ref={navRef}>
              <div className={`nav-base ${isOpen ? 'is-expanded' : ''}`}>
                {/* Top row: Logo + Mobile Toggle */}
                <div className="nav-base-top">
                  <a href="https://www.azaistudios.com" className="nav-brand" aria-label="Azai Studios Home">
                    <img 
                      src="https://cdn.prod.website-files.com/651046383755a268828ba9f2/65119cb5fc2c272359aa2139_Azai%20Studios.svg" 
                      loading="lazy" 
                      alt="Azai Studios Logo"
                      className="nav-logo-img"
                    />
                  </a>

                  {/* Mobile Menu Toggle - only visible on mobile */}
                  <a 
                    href="#"
                    className="nav-icon-wrapper"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsOpen(!isOpen);
                    }}
                    aria-label="Toggle menu"
                    aria-expanded={isOpen}
                  >
                    <div className={`nav-plus-icon ${isOpen ? 'is-open' : ''}`}>
                      <span className="nav-plus-line nav-plus-horizontal"></span>
                      <span className="nav-plus-line nav-plus-vertical"></span>
                    </div>
                  </a>
                </div>

                {/* Desktop Navigation Links - separate row, visible on desktop */}
                <div className="flex-horizontal nav-links-row">
                  <div className="nav-links">
                    {navLinks.map((link, index) => (
                      <a 
                        key={link.href}
                        href={link.href} 
                        className={`nav-link-wrapper ${index === 0 ? 'first-margin' : ''}`}
                      >
                        <div className="nav-elipse"></div>
                        <div className="text-style-nowrap">{link.label}</div>
                      </a>
                    ))}
                  </div>
                  <div className="nav-spacer"></div>
                </div>

                {/* Mobile Navigation Dropdown */}
                <div className={`nav-dropdown ${isOpen ? 'is-open' : ''}`}>
                  <div className="nav-links-mobile">
                    {navLinks.map((link) => (
                      <a 
                        key={link.href}
                        href={link.href} 
                        className="nav-link-wrapper"
                        onClick={() => setIsOpen(false)}
                      >
                        <div className="nav-elipse"></div>
                        <div className="text-style-nowrap">{link.label}</div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </nav>
          </div>
        </div>
      </div>
    </section>
  );
}
